import { fetchJSON } from './http';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Simple HTTP client wrapper
const http = {
  get: async (url: string) => {
    const data = await fetchJSON<any>(`${API_BASE_URL}/api${url}`);
    return { data };
  },
  post: async (url: string, body?: any) => {
    const data = await fetchJSON<any>(`${API_BASE_URL}/api${url}`, {
      method: 'POST',
      body
    });
    return { data };
  },
  patch: async (url: string, body?: any) => {
    const data = await fetchJSON<any>(`${API_BASE_URL}/api${url}`, {
      method: 'PATCH',
      body
    });
    return { data };
  }
};

export interface Slot {
  start: string;
  end: string;
  slotSizeMin: number;
  slotCount: number;
  practitionerId?: string;
  isAvailable: boolean;
  reason?: string;
}

export interface PlanningBookingRequest {
  clientId: string;
  companyId?: string;
  shopId: string;
  items: Array<{
    typeId: string;
    zoneId: string;
  }>;
  preferredStart: string;
  minor?: boolean;
  timezone?: string;
}

export interface PlanningBookingResponse {
  success: boolean;
  message: string;
  data: {
    appointmentId: string;
    companyName: string;
    shopName: string;
    practitionerName: string;
    scheduledAt: string;
    slotCount: number;
  };
}

export interface SlotRequest {
  companyId?: string;
  shopId: string;
  practitionerId?: string;
  serviceId?: string;
  piercingTypeId?: string;
  piercingTypeIds?: string[];
  from: string;
  to: string;
  minor?: boolean;
  includeCrossShopPractitioners?: boolean;
}

export interface SlotResponse {
  success: boolean;
  data: {
    slots: Slot[];
    practitioners: Array<{
      practitionerId: string;
      name: string;
      relationshipType: string;
      hourlyRate?: number;
    }>;
    shopTimezone: string;
    slotSizeMinutes: number;
    slotSizeAdditionalActMinutes?: number;
    requiredDurationMinutes?: number;
    piercingDurations?: Array<{
      piercingTypeId: string | null;
      durationMinutes: number;
      source: 'shop_default' | 'shop_additional' | 'type_override' | 'type_minor_override';
    }>;
    totalSlotsGenerated: number;
    availableSlots: number;
    companyContext?: {
      companyId: string;
      companyName: string;
      shopName: string;
    };
  };
}

export interface Practitioner {
  id: string;
  firstName: string;
  lastName: string;
  artistName?: string;
  relationshipType: string;
  hourlyRate?: number;
  specialties: any;
  bio: string;
  experience: number;
  rating: number;
  reviewCount: number;
}

export interface ShopWithPlanning {
  id: string;
  name: string;
  address: any;
  phone: string;
  email: string;
  website?: string;
  socialMedia: any;
  openingHours: any;
  services: any;
  facilities: any;
  policies: any;
  timezone: string;
  defaultSlotSizeMinutes: number;
  companyId: string;
  companyName?: string;
}

export class PlanningApi {
  /**
   * Get available slots for a shop
   */
  static async getSlots(request: SlotRequest): Promise<SlotResponse> {
    const params = new URLSearchParams();
    
    if (request.companyId) params.append('companyId', request.companyId);
    params.append('shopId', request.shopId);
    if (request.practitionerId) params.append('practitionerId', request.practitionerId);
    if (request.serviceId) params.append('serviceId', request.serviceId);
    if (request.piercingTypeId) params.append('piercingTypeId', request.piercingTypeId);
    if (request.piercingTypeIds?.length) params.append('piercingTypeIds', request.piercingTypeIds.join(','));
    params.append('from', request.from);
    params.append('to', request.to);
    if (request.minor) params.append('minor', request.minor.toString());
    if (request.includeCrossShopPractitioners) params.append('includeCrossShopPractitioners', request.includeCrossShopPractitioners.toString());

    const response = await http.get(`/planning/slots?${params.toString()}`);
    return response.data;
  }

  /**
   * Create appointment using planning system
   */
  static async createAppointment(request: PlanningBookingRequest): Promise<PlanningBookingResponse> {
    const response = await http.post('/planning/appointments', request);
    return response.data;
  }

  /**
   * Cancel appointment with 24h rule
   */
  static async cancelAppointment(
    appointmentId: string, 
    reason?: string, 
    refundAmount?: number,
    timezone: string = 'Europe/Paris'
  ) {
    const response = await http.post(`/planning/appointments/${appointmentId}/cancel`, {
      reason,
      refundAmount,
      timezone
    });
    return response.data;
  }

  /**
   * Confirm appointment
   */
  static async confirmAppointment(
    appointmentId: string,
    newScheduledAt?: string,
    timezone: string = 'Europe/Paris'
  ) {
    const response = await http.patch(`/planning/appointments/${appointmentId}/confirm`, {
      newScheduledAt,
      timezone
    });
    return response.data;
  }

  /**
   * Get shops with planning capabilities
   */
  static async getShopsWithPlanning(): Promise<{ success: boolean; data: { shops: ShopWithPlanning[] } }> {
    const response = await http.get('/shops');
    return response.data;
  }

  /**
   * Get practitioners for a shop
   */
  static async getShopPractitioners(shopId: string): Promise<{ success: boolean; data: { practitioners: Practitioner[] } }> {
    const response = await http.get(`/planning/practitioners/shop/${shopId}`);
    return response.data;
  }

  /**
   * Get shop schedule rules
   */
  static async getShopScheduleRules(shopId: string): Promise<{ success: boolean; data: { rules: any[] } }> {
    const response = await http.get(`/planning/schedule-rules/shop/${shopId}`);
    return response.data;
  }

  /**
   * Validate appointment time constraints
   */
  static async validateAppointmentTime(
    shopId: string,
    appointmentTime: string,
    timezone: string = 'Europe/Paris'
  ): Promise<{ isValid: boolean; reason?: string }> {
    try {
      // This would typically be a dedicated endpoint, but for now we'll use the slots endpoint
      // to validate if the time is available
      const slots = await this.getSlots({
        shopId,
        from: appointmentTime,
        to: new Date(new Date(appointmentTime).getTime() + 60 * 60 * 1000).toISOString() // +1 hour
      });

      const requestedTime = new Date(appointmentTime);
      const availableSlot = slots.data.slots.find(slot => {
        const slotStart = new Date(slot.start);
        const slotEnd = new Date(slot.end);
        return slot.isAvailable && requestedTime >= slotStart && requestedTime < slotEnd;
      });

      return {
        isValid: !!availableSlot,
        reason: availableSlot ? undefined : 'No available slots at the requested time'
      };
    } catch (error) {
      return {
        isValid: false,
        reason: 'Unable to validate appointment time'
      };
    }
  }

  /**
   * Get alternative time suggestions
   */
  static async getAlternativeTimes(
    shopId: string,
    preferredTime: string,
    serviceDurationMinutes: number = 30,
    daysToSearch: number = 7
  ): Promise<Slot[]> {
    const preferredDate = new Date(preferredTime);
    const searchStart = new Date(preferredDate);
    searchStart.setDate(searchStart.getDate() - 1);
    const searchEnd = new Date(preferredDate);
    searchEnd.setDate(searchEnd.getDate() + daysToSearch);

    const slots = await this.getSlots({
      shopId,
      from: searchStart.toISOString(),
      to: searchEnd.toISOString()
    });

    // Filter for slots that can accommodate the service duration
    return slots.data.slots.filter(slot => {
      const slotDuration = slot.slotCount * slot.slotSizeMin;
      return slot.isAvailable && slotDuration >= serviceDurationMinutes;
    }).slice(0, 10); // Return top 10 alternatives
  }

  /**
   * Calculate service duration based on piercing items
   */
  static calculateServiceDuration(piercingItems: Array<{ typeId: string; estimatedDuration?: number }>): number {
    return piercingItems.reduce((total, item) => {
      return total + (item.estimatedDuration || 30); // Default 30 minutes per piercing
    }, 0);
  }

  /**
   * Format slot time for display
   */
  static formatSlotTime(slot: Slot, timezone: string = 'Europe/Paris'): string {
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    
    return start.toLocaleString('fr-FR', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Check if slot is for minors (30+ minutes)
   */
  static isMinorSlot(slot: Slot): boolean {
    return slot.slotSizeMin >= 30;
  }

  /**
   * Get slot duration in minutes
   */
  static getSlotDuration(slot: Slot): number {
    return slot.slotCount * slot.slotSizeMin;
  }

  /**
   * Check if current time is within booking constraints
   */
  static async checkBookingConstraints(
    shopId: string,
    appointmentTime: string,
    timezone: string = 'Europe/Paris'
  ): Promise<{
    canBook: boolean;
    reason?: string;
    constraints?: {
      minAdvanceHours: number;
      maxAdvanceDays: number;
      slotSizeMinutes: number;
    };
  }> {
    try {
      const rules = await this.getShopScheduleRules(shopId);
      const defaultRule = rules.data.rules.find((rule: any) => rule.ruleType === 'DEFAULT');
      
      if (!defaultRule) {
        return { canBook: true }; // No constraints if no rules
      }

      const appointmentDate = new Date(appointmentTime);
      const now = new Date();
      const hoursUntilAppointment = (appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      const daysUntilAppointment = hoursUntilAppointment / 24;

      const constraints = {
        minAdvanceHours: defaultRule.minAdvanceBookingHours || 2,
        maxAdvanceDays: defaultRule.maxAdvanceBookingDays || 90,
        slotSizeMinutes: defaultRule.slotSizeMinutes || 15
      };

      if (hoursUntilAppointment < constraints.minAdvanceHours) {
        return {
          canBook: false,
          reason: `Appointment must be at least ${constraints.minAdvanceHours} hours in advance`,
          constraints
        };
      }

      if (daysUntilAppointment > constraints.maxAdvanceDays) {
        return {
          canBook: false,
          reason: `Appointment cannot be more than ${constraints.maxAdvanceDays} days in advance`,
          constraints
        };
      }

      return { canBook: true, constraints };
    } catch (error) {
      console.error('Failed to check booking constraints:', error);
      return { canBook: true }; // Allow booking if constraints check fails
    }
  }
}
